import { useState, useEffect } from 'react';
import { useLocation, useRoute } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Mail, ArrowLeft, Plus, Trash2, Search, Image, Link as LinkIcon, Calendar, ExternalLink, GripVertical, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/use-auth';
import { useToast } from '@/hooks/use-toast';
import { getNewsletter, addNewsletter, updateNewsletter, getCommunityEvents, getAllContent, getPrograms, uploadNewsletterImage, Timestamp } from '@/lib/firebase';
import { logger } from '@/lib/logger';

interface LeadArticleLink {
  text: string;
  url: string;
}

interface LeadArticle {
  title: string;
  imageUrl: string;
  text: string[];
  links: LeadArticleLink[];
}

interface ContentItem {
  contentId: string;
  title: string;
  thumbnailUrl: string;
  description: string;
  modules: string[];
  contentLibraries: string[];
  createdDate: string;
}

interface Feature {
  featureType: 'program' | 'event' | 'content';
  sourceId: string;
  title: string;
  imageUrl: string;
  description: string;
  featureText: string;
  featureTag: string;
  displayOrder: number;
  tags: string[];
}

interface EventCalendarItem {
  eventId: string;
  eventType: string;
  title: string;
  dateTime: string;
  subtitle: string;
  imageUrl: string;
}

interface Newsletter {
  emailSubject: string;
  emailSubheading: string;
  newsletterDate: string;
  leadArticle: LeadArticle;
  features: Feature[];
  eventCalendar: EventCalendarItem[];
}

export default function AddNewsletterPage() {
  const [, setLocation] = useLocation();
  const [match, params] = useRoute('/newsletters/edit/:id');
  const isEditing = match && params?.id;
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [emailSubject, setEmailSubject] = useState('');
  const [emailSubheading, setEmailSubheading] = useState('');
  const [newsletterDate, setNewsletterDate] = useState(new Date().toISOString().split('T')[0]);

  const [leadArticle, setLeadArticle] = useState<LeadArticle>({
    title: '',
    imageUrl: '',
    text: [''],
    links: []
  });

  const [features, setFeatures] = useState<Feature[]>([]);
  const [eventCalendar, setEventCalendar] = useState<EventCalendarItem[]>([]);

  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [featureType, setFeatureType] = useState<'program' | 'event' | 'content'>('content');
  const [availableContent, setAvailableContent] = useState<any[]>([]);
  const [availablePrograms, setAvailablePrograms] = useState<any[]>([]);
  const [availableEvents, setAvailableEvents] = useState<any[]>([]);
  const [featureSearchTerm, setFeatureSearchTerm] = useState('');

  useEffect(() => {
    if (isEditing && params?.id) {
      loadNewsletter(params.id);
    }
  }, [isEditing, params?.id]);

  useEffect(() => {
    if (newsletterDate) {
      loadEventsForCalendar(newsletterDate);
    }
  }, [newsletterDate]);

  const loadNewsletter = async (id: string) => {
    setLoading(true);
    try {
      const data = await getNewsletter(id);
      
      if (data) {
        setEmailSubject(data.emailSubject || '');
        setEmailSubheading(data.emailSubheading || '');
        
        if (data.newsletterDate) {
          if (data.newsletterDate instanceof Timestamp) {
            setNewsletterDate(data.newsletterDate.toDate().toISOString().split('T')[0]);
          } else {
            setNewsletterDate(data.newsletterDate);
          }
        }
        
        setLeadArticle(data.leadArticle || { title: '', imageUrl: '', text: [''], links: [] });

        setFeatures(data.features || []);
        setEventCalendar(data.eventCalendar || []);
      }
    } catch (error: any) {
      logger.error('Error loading newsletter:', error);
      toast({
        title: 'Failed to load newsletter',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadEventsForCalendar = async (date: string) => {
    try {
      const startDate = new Date(date);
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 30);

      const allEvents = await getCommunityEvents() as any[];
      const events = allEvents
        .filter(e => {
          const evDate = e.startDate instanceof Timestamp ? e.startDate.toDate() : (e.startDate ? new Date(e.startDate) : null);
          return evDate && evDate >= startDate && evDate <= endDate;
        })
        .sort((a, b) => {
          const aDate = a.startDate instanceof Timestamp ? a.startDate.toDate() : new Date(a.startDate || 0);
          const bDate = b.startDate instanceof Timestamp ? b.startDate.toDate() : new Date(b.startDate || 0);
          return aDate.getTime() - bDate.getTime();
        })
        .map(e => ({
          eventId: e.id,
          eventType: e.eventType || 'General',
          title: e.title || '',
          dateTime: e.startDate instanceof Timestamp ? e.startDate.toDate().toISOString() : e.startDate || '',
          subtitle: e.description || '',
          imageUrl: e.backgroundImageUrl || ''
        }));

      setEventCalendar(events);
      logger.log('Loaded events for calendar:', events.length);
    } catch (error: any) {
      logger.error('Error loading events:', error);
    }
  };

  const loadAllContent = async () => {
    try {
      const content = await getAllContent();
      setAvailableContent(content as any[]);
      logger.log('Loaded all content for features:', content.length);
    } catch (error: any) {
      logger.error('Error loading all content:', error);
    }
  };

  const loadAvailablePrograms = async () => {
    try {
      const programs = await getPrograms();
      setAvailablePrograms(programs as any[]);
    } catch (error: any) {
      logger.error('Error loading programs:', error);
    }
  };

  const loadAvailableEvents = async () => {
    try {
      const events = await getCommunityEvents();
      setAvailableEvents(events as any[]);
    } catch (error: any) {
      logger.error('Error loading events:', error);
    }
  };

  const handleOpenFeatureModal = (type: 'program' | 'event' | 'content') => {
    setFeatureType(type);
    if (type === 'program') {
      loadAvailablePrograms();
    } else if (type === 'event') {
      loadAvailableEvents();
    } else {
      loadAllContent();
    }
    setFeatureModalOpen(true);
  };

  const handleAddFeature = (item: any) => {
    let imageUrl = '';
    if (featureType === 'event') {
      imageUrl = item.backgroundImageUrl || '';
    } else if (featureType === 'program') {
      imageUrl = item.bannerUrl || '';
    } else {
      imageUrl = item.thumbnailUrl || '';
    }

    const feature: Feature = {
      featureType,
      sourceId: item.id,
      title: item.title || item.name || '',
      imageUrl,
      description: item.description || item.subtitle || '',
      featureText: '',
      featureTag: '',
      displayOrder: features.length + 1,
      tags: item.tags || []
    };

    setFeatures([...features, feature]);
    setFeatureModalOpen(false);
  };

  const handleRemoveFeature = (index: number) => {
    const updated = features.filter((_, i) => i !== index);
    setFeatures(updated.map((f, i) => ({ ...f, displayOrder: i + 1 })));
  };

  const handleMoveFeatureUp = (index: number) => {
    if (index === 0) return;
    const updated = [...features];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setFeatures(updated.map((f, i) => ({ ...f, displayOrder: i + 1 })));
  };

  const handleMoveFeatureDown = (index: number) => {
    if (index === features.length - 1) return;
    const updated = [...features];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setFeatures(updated.map((f, i) => ({ ...f, displayOrder: i + 1 })));
  };

  const handleUpdateFeatureText = (index: number, value: string) => {
    const updated = [...features];
    updated[index] = { ...updated[index], featureText: value };
    setFeatures(updated);
  };

  const handleUpdateFeatureTag = (index: number, value: string) => {
    const updated = [...features];
    updated[index] = { ...updated[index], featureTag: value };
    setFeatures(updated);
  };

  const handleAddParagraph = () => {
    setLeadArticle({
      ...leadArticle,
      text: [...leadArticle.text, '']
    });
  };

  const handleUpdateParagraph = (index: number, value: string) => {
    const newText = [...leadArticle.text];
    newText[index] = value;
    setLeadArticle({ ...leadArticle, text: newText });
  };

  const handleRemoveParagraph = (index: number) => {
    if (leadArticle.text.length <= 1) return;
    setLeadArticle({
      ...leadArticle,
      text: leadArticle.text.filter((_, i) => i !== index)
    });
  };

  const handleAddLink = () => {
    setLeadArticle({
      ...leadArticle,
      links: [...leadArticle.links, { text: '', url: '' }]
    });
  };

  const handleUpdateLink = (index: number, field: 'text' | 'url', value: string) => {
    const newLinks = [...leadArticle.links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setLeadArticle({ ...leadArticle, links: newLinks });
  };

  const handleRemoveLink = (index: number) => {
    setLeadArticle({
      ...leadArticle,
      links: leadArticle.links.filter((_, i) => i !== index)
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const url = await uploadNewsletterImage(file);
      setLeadArticle({ ...leadArticle, imageUrl: url });
      toast({ title: 'Image uploaded successfully' });
    } catch (error: any) {
      logger.error('Error uploading image:', error);
      toast({
        title: 'Failed to upload image',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleSave = async () => {
    if (!emailSubject.trim()) {
      toast({ title: 'Email subject is required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const newsletterData = {
        emailSubject: emailSubject.trim(),
        emailSubheading: emailSubheading.trim(),
        newsletterDate: Timestamp.fromDate(new Date(newsletterDate)),
        leadArticle,
        features,
        eventCalendar,
        updated: Timestamp.now()
      };

      if (isEditing && params?.id) {
        await updateNewsletter(params.id, newsletterData);
        toast({ title: 'Newsletter updated successfully' });
      } else {
        await addNewsletter({ ...newsletterData, created: Timestamp.now() });
        toast({ title: 'Newsletter created successfully' });
      }

      setLocation('/newsletters');
    } catch (error: any) {
      logger.error('Error saving newsletter:', error);
      toast({
        title: 'Failed to save newsletter',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const getFeatureItems = () => {
    if (featureType === 'program') {
      return availablePrograms.filter(p =>
        p.name?.toLowerCase().includes(featureSearchTerm.toLowerCase())
      );
    } else if (featureType === 'event') {
      return availableEvents.filter(e =>
        e.title?.toLowerCase().includes(featureSearchTerm.toLowerCase())
      );
    } else {
      return availableContent.filter(c =>
        c.title?.toLowerCase().includes(featureSearchTerm.toLowerCase())
      );
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading newsletter...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8 overflow-y-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => setLocation('/newsletters')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Mail className="h-6 w-6" />
                {isEditing ? 'Edit Newsletter' : 'Add Newsletter'}
              </h1>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Newsletter'
            )}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>General</CardTitle>
            <CardDescription>Basic newsletter information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emailSubject">Email Subject *</Label>
              <Input
                id="emailSubject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emailSubheading">Email Subheading</Label>
              <Input
                id="emailSubheading"
                value={emailSubheading}
                onChange={(e) => setEmailSubheading(e.target.value)}
                placeholder="Enter email subheading"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newsletterDate">Newsletter Date</Label>
              <Input
                id="newsletterDate"
                type="date"
                value={newsletterDate}
                onChange={(e) => setNewsletterDate(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Lead Article</CardTitle>
            <CardDescription>Main featured article for this newsletter</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="leadTitle">Title</Label>
              <Input
                id="leadTitle"
                value={leadArticle.title}
                onChange={(e) => setLeadArticle({ ...leadArticle, title: e.target.value })}
                placeholder="Enter article title"
              />
            </div>

            <div className="space-y-2">
              <Label>Image</Label>
              <div className="flex items-start gap-4">
                <div className="w-32 h-20 rounded overflow-hidden flex-shrink-0 bg-[#2d2d44]">
                  {leadArticle.imageUrl ? (
                    <img src={leadArticle.imageUrl} alt="Lead article" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-8 h-8 text-white/30" />
                    </div>
                  )}
                </div>
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-auto"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Paragraphs</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddParagraph}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Paragraph
                </Button>
              </div>
              {leadArticle.text.map((paragraph, index) => (
                <div key={index} className="flex gap-2">
                  <Textarea
                    value={paragraph}
                    onChange={(e) => handleUpdateParagraph(index, e.target.value)}
                    placeholder={`Paragraph ${index + 1}`}
                    rows={3}
                  />
                  {leadArticle.text.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveParagraph(index)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Links</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddLink}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Link
                </Button>
              </div>
              {leadArticle.links.map((link, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <Input
                      value={link.text}
                      onChange={(e) => handleUpdateLink(index, 'text', e.target.value)}
                      placeholder="Link text"
                    />
                    <Input
                      value={link.url}
                      onChange={(e) => handleUpdateLink(index, 'url', e.target.value)}
                      placeholder="URL"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveLink(index)}
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Features</CardTitle>
                <CardDescription>Featured programs, events, or content</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleOpenFeatureModal('program')}>
                  <Plus className="h-4 w-4 mr-1" />
                  Program
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleOpenFeatureModal('event')}>
                  <Plus className="h-4 w-4 mr-1" />
                  Event
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleOpenFeatureModal('content')}>
                  <Plus className="h-4 w-4 mr-1" />
                  Content
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {features.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No features added yet</p>
            ) : (
              <div className="space-y-3">
                {features.map((feature, index) => (
                  <div key={index} className="p-3 border rounded-lg space-y-2">
                    <div className="flex items-center gap-4">
                      {feature.imageUrl ? (
                        <img src={feature.imageUrl} alt={feature.title} className="w-16 h-12 object-cover rounded" />
                      ) : (
                        <div className="w-16 h-12 bg-slate-100 rounded flex items-center justify-center">
                          <Image className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{feature.featureType}</Badge>
                          <span className="font-medium">{feature.title}</span>
                        </div>
                        {feature.description && (
                          <div className="text-sm text-slate-500 line-clamp-1">{feature.description}</div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleMoveFeatureUp(index)} disabled={index === 0}>
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleMoveFeatureDown(index)} disabled={index === features.length - 1}>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveFeature(index)} className="text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={feature.featureText || ''}
                        onChange={(e) => handleUpdateFeatureText(index, e.target.value)}
                        placeholder="Custom feature text..."
                        className="text-sm flex-1"
                      />
                      <Input
                        value={feature.featureTag || ''}
                        onChange={(e) => handleUpdateFeatureTag(index, e.target.value)}
                        placeholder="Tag (e.g. New Series)"
                        className="text-sm w-48"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Event Calendar</CardTitle>
            <CardDescription>Events in the 30 days from newsletter date (auto-populated)</CardDescription>
          </CardHeader>
          <CardContent>
            {eventCalendar.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No events found in the date range</p>
            ) : (
              <div className="space-y-2">
                {eventCalendar.map((event, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 border rounded-lg">
                    {event.imageUrl ? (
                      <img src={event.imageUrl} alt={event.title} className="w-16 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-16 h-12 bg-slate-100 rounded flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{event.eventType}</Badge>
                        <span className="font-medium">{event.title}</span>
                      </div>
                      <div className="text-sm text-slate-500">
                        {event.dateTime ? new Date(event.dateTime).toLocaleDateString() : 'No date'}
                        {event.subtitle && ` - ${event.subtitle}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={featureModalOpen} onOpenChange={setFeatureModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select {featureType.charAt(0).toUpperCase() + featureType.slice(1)}</DialogTitle>
            <DialogDescription>Choose a {featureType} to feature</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
              <Input
                placeholder={`Search ${featureType}s...`}
                value={featureSearchTerm}
                onChange={(e) => setFeatureSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {getFeatureItems().length === 0 ? (
                <p className="text-slate-500 text-center py-4">No {featureType}s found</p>
              ) : (
                getFeatureItems().map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-4 p-3 border rounded-lg cursor-pointer hover:bg-slate-50"
                    onClick={() => handleAddFeature(item)}
                  >
                    {(featureType === 'event' ? item.backgroundImageUrl : featureType === 'program' ? item.bannerUrl : item.thumbnailUrl) ? (
                      <img src={featureType === 'event' ? item.backgroundImageUrl : featureType === 'program' ? item.bannerUrl : item.thumbnailUrl} alt={item.title || item.name} className="w-16 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-16 h-12 bg-slate-100 rounded flex items-center justify-center">
                        <Image className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="font-medium">{item.title || item.name}</div>
                      {(item.description || item.subtitle) && (
                        <div className="text-sm text-slate-500 line-clamp-1">{item.description || item.subtitle}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
